/**
 * @file MapSidebar/index.tsx
 * @description Main sidebar container for map pages
 * Contains filters and parks list in a single wide sidebar (380px desktop, 85vw mobile)
 * Mobile: Slide-out panel with toggle button
 */

'use client';

import { useState, useEffect, useRef } from 'react';
import { Bikepark, Snowpark, Pumptrack } from '@/types';
import FiltersWrapper from '@/components/FiltersWrapper';
import ParksList from '@/components/ParksList';
import SidebarToggle from './SidebarToggle';
import { Bike, Flag, Snowflake, ChevronDown, ChevronUp } from 'lucide-react';

interface MapSidebarProps {
  parks: (Bikepark | Snowpark | Pumptrack)[];
  filteredParks: (Bikepark | Snowpark | Pumptrack)[];
  onFilterChange: (filtered: (Bikepark | Snowpark | Pumptrack)[]) => void;
  onParkClick: (park: Bikepark | Snowpark | Pumptrack) => void;
  onParkHover?: (park: Bikepark | Snowpark | Pumptrack) => void;
  onParkHoverEnd?: () => void;
  type: 'bikepark' | 'snowpark' | 'pumptrack';
}

export default function MapSidebar({
  parks,
  filteredParks,
  onFilterChange,
  onParkClick,
  onParkHover,
  onParkHoverEnd,
  type,
}: MapSidebarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const resetFiltersRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // On desktop, always show sidebar
      if (!mobile) {
        setIsOpen(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleParkClick = (park: Bikepark | Snowpark) => {
    onParkClick(park);

    // Close sidebar on mobile after selecting park
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Toggle Button - Mobile Only */}
      <SidebarToggle isOpen={isOpen} onToggle={handleToggle} />

      {/* Overlay - Mobile Only */}
      {isMobile && isOpen && (
        <div
          className="map-sidebar-overlay"
          onClick={() => setIsOpen(false)}
          aria-label="Close sidebar"
        />
      )}

      {/* Sidebar */}
      <aside className={`map-sidebar ${isOpen ? 'open' : ''} ${isMobile ? 'mobile' : 'desktop'}`}>
        <div className="map-sidebar-inner">

          {/* Header */}
          <div className="map-sidebar-header">
            <h2 className="map-sidebar-title">
              {type === 'bikepark' ? (
                <><Bike size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} /> Filtry</>
              ) : type === 'pumptrack' ? (
                <><Flag size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} /> Filtry</>
              ) : (
                <><Snowflake size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} /> Filtry</>
              )}
            </h2>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Toggle filters button */}
              <button
                onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                className="map-sidebar-toggle-filters"
                title={filtersCollapsed ? 'Rozwiń filtry' : 'Zwiń filtry'}
              >
                {filtersCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>
          </div>

          {/* Filters Section */}
          {!filtersCollapsed && (
            <div className="map-sidebar-filters">
              <FiltersWrapper
                parks={parks}
                onFilterChange={onFilterChange}
                type={type}
                onResetRef={resetFiltersRef}
              />
            </div>
          )}

          {/* Divider */}
          {!filtersCollapsed && <div className="map-sidebar-divider" />}

          {/* Parks List Section */}
          <div className="map-sidebar-list">
            <div className="map-sidebar-list-header">
              <h3>Lista parków ({filteredParks.length})</h3>
            </div>

            <div className="map-sidebar-list-scroll">
              <ParksList
                parks={filteredParks}
                onParkClick={handleParkClick}
                onParkHover={onParkHover}
                onParkHoverEnd={onParkHoverEnd}
                type={type}
              />
            </div>
          </div>
        </div>
      </aside>

      <style jsx>{`
        /* Overlay */
        .map-sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 998;
          backdrop-filter: blur(2px);
          animation: fadeIn 200ms ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Sidebar Container - Green theme */
        .map-sidebar {
          background: rgba(241, 248, 244, 0.98);
          border-right: 1px solid rgba(16, 185, 129, 0.2);

          display: flex;
          flex-direction: column;

          box-shadow: 4px 0 12px rgba(0, 0, 0, 0.1);

          /* Animation */
          transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Mobile State */
        .map-sidebar.mobile {
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          width: 85vw;
          max-width: 400px;
          z-index: 999;
          transform: translateX(-100%);
        }

        .map-sidebar.mobile.open {
          transform: translateX(0);
        }

        /* Desktop State */
        .map-sidebar.desktop {
          position: relative;
          width: 420px;
          transform: none;
          z-index: 1;
        }

        /* Inner Container */
        .map-sidebar-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
        }

        /* Header */
        .map-sidebar-header {
          padding: 16px 20px;
          border-bottom: 1px solid rgba(16, 185, 129, 0.2);
          background: rgba(255, 255, 255, 0.5);

          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;

          flex-shrink: 0;
        }

        .map-sidebar-title {
          font-size: 18px;
          font-weight: 600;
          color: #1a3a2e;
          margin: 0;
        }

        .map-sidebar-toggle-filters {
          background: rgba(16, 185, 129, 0.12);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 6px;
          color: #059669;
          cursor: pointer;
          font-size: 14px;
          padding: 6px 10px;
          transition: all 150ms ease;

          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }

        .map-sidebar-toggle-filters:hover {
          background: rgba(16, 185, 129, 0.2);
          border-color: rgba(16, 185, 129, 0.5);
          transform: scale(1.05);
        }

        .map-sidebar-toggle-filters:active {
          transform: scale(0.95);
        }

        /* Filters Section */
        .map-sidebar-filters {
          padding: 20px;
          overflow-y: auto;
          flex-shrink: 0;

          /* Custom scrollbar */
          scrollbar-width: thin;
          scrollbar-color: rgba(16, 185, 129, 0.3) transparent;
        }

        .map-sidebar-filters::-webkit-scrollbar {
          width: 6px;
        }

        .map-sidebar-filters::-webkit-scrollbar-track {
          background: transparent;
        }

        .map-sidebar-filters::-webkit-scrollbar-thumb {
          background: rgba(16, 185, 129, 0.3);
          border-radius: 3px;
        }

        .map-sidebar-filters::-webkit-scrollbar-thumb:hover {
          background: rgba(16, 185, 129, 0.5);
        }

        /* Divider */
        .map-sidebar-divider {
          height: 1px;
          background: linear-gradient(
            to right,
            transparent,
            rgba(16, 185, 129, 0.3),
            transparent
          );
          margin: 0 20px;
          flex-shrink: 0;
        }

        /* Parks List Section */
        .map-sidebar-list {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          min-height: 0;
        }

        .map-sidebar-list-header {
          padding: 16px 20px 12px;
          border-bottom: 1px solid rgba(16, 185, 129, 0.2);
          background: rgba(248, 250, 249, 0.5);
          flex-shrink: 0;
        }

        .map-sidebar-list-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1a3a2e;
        }

        .map-sidebar-list-scroll {
          flex: 1;
          overflow-y: auto;
          padding: 12px;

          /* Custom scrollbar */
          scrollbar-width: thin;
          scrollbar-color: rgba(16, 185, 129, 0.3) transparent;
        }

        .map-sidebar-list-scroll::-webkit-scrollbar {
          width: 6px;
        }

        .map-sidebar-list-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .map-sidebar-list-scroll::-webkit-scrollbar-thumb {
          background: rgba(16, 185, 129, 0.3);
          border-radius: 3px;
        }

        .map-sidebar-list-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(16, 185, 129, 0.5);
        }

        /* Tablet */
        @media (min-width: 768px) and (max-width: 1023px) {
          .map-sidebar.desktop {
            width: 340px;
          }
        }

        /* Large Desktop */
        @media (min-width: 1440px) {
          .map-sidebar.desktop {
            width: 420px;
          }
        }

        /* Small Phones */
        @media (max-width: 375px) {
          .map-sidebar.mobile {
            width: 90vw;
          }

          .map-sidebar-header {
            padding: 12px 16px;
          }

          .map-sidebar-filters,
          .map-sidebar-list-header {
            padding-left: 16px;
            padding-right: 16px;
          }
        }
      `}</style>
    </>
  );
}
